import * as React from 'react'
import { Link, LinkProps } from '@amzn/dvui/dist/Link'
import { Spinner } from '@amzn/dvui/dist/Spinner'
import { Episode } from '../episode/episode'
import { CollectionPagination } from '../collection/pagination'
import * as st from './episodeList.module.scss'
import { CollectionType } from '../collection/collectionTypes'
import { TextLocalizer, StringId } from '../localizer/localizer'
import { IDetailPageState } from '../../reducers'
import { IRequestContext, sanitizedRequestContextSelector } from '../../selectors/requestContext'
import { requestEpisodes } from '../../utils/wps'
import { connect } from 'react-redux'
import { detailPagePath, MakeUrlArgs } from '../../utils/url'
import {
  episodesAvailableSelector,
  episodesLeavingSoonSelector,
  episodesSeenSelector,
  TitleIdToAvailable
} from '../../selectors/actionFragment'
import { EXPANDER_THRESHOLD } from '../../reducers/collections'
import { CloseableDropList } from '../../utils/closeableDropList'
import { ref } from '@amzn/dv-web-node-reftags'
import { DpWidgetContext } from '../../utils/dpWidgetContext'
import { ILogger, withLogger } from '../../context/withLogger'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { logRefTag } from '@utils'

export const EPISODE_START_INDEX = 0
const DROP_LIST_ID = 'sorting-droplist'

//TODO(AVWebDP-285) Add IMDB Rating
export enum SortingType {
  EpisodeNumberAscending = 'EpisodeNumberAscending',
  EpisodeNumberDescending = 'EpisodeNumberDescending',
  Available = 'Available',
}

export const TYPE_TO_STRING_ID = {
  EpisodeNumberAscending: 'DV_DP_EPISODE_NUM_ASCENDING',
  EpisodeNumberDescending: 'DV_DP_EPISODE_NUM_DESCENDING',
  Available: 'DV_DP_EPISODE_AVAILABLE',
} as Record<SortingType, StringId>

export const TYPE_TO_REFTAG = {
  EpisodeNumberAscending: ref('atv_dp_ep_ena'),
  EpisodeNumberDescending: ref('atv_dp_ep_end'),
  Available: ref('atv_dp_ep_av'),
} as Record<SortingType, string>

interface SortingFunc {
  (titleIds: string[], titleIdToAvailable: TitleIdToAvailable): string[]
}

/* The Episodes are naturally ordered by Episode number ascending */
export const TYPE_TO_SORT_FUNC = {
  EpisodeNumberAscending: (titleIds: string[]) => titleIds,
  EpisodeNumberDescending: (titleIds: string[]) => [...titleIds].reverse(),
  // Split into available and unavailable
  Available: (titleIds: string[], titleIdToAvailable: TitleIdToAvailable) => {
    const availableTitles = titleIds.filter((titleId) => titleIdToAvailable[titleId])
    const unavailableTitles = titleIds.filter((titleId) => !titleIdToAvailable[titleId])
    return [
      ...availableTitles,
      ...unavailableTitles
    ]
  },
} as Record<SortingType, SortingFunc>

/* Dictates the order of the Sorting Options */
export const TYPE_LIST = [
  SortingType.EpisodeNumberAscending,
  SortingType.EpisodeNumberDescending,
  SortingType.Available
]

export interface OwnProps {
  header?: string
  collectionType: CollectionType
  totalEpisodeSize: number
  cardTitleIds: string[]
}

export interface IStateProps {
  requestContext: IRequestContext
  pageTitleId: string
  isElcano: boolean
  episodesSeen: number
  titleIdToAvailable: TitleIdToAvailable,
  episodesLeavingSoon: number,
  enableLeavingSoonBtf: boolean
}

export type EpisodeListProps = OwnProps & IStateProps & ILogger

export interface EpisodeListState {
  episodesHidden: boolean,
  sortingState: SortingType,
  sortingChanged: boolean
}

export const dropdownText = (sortingType: SortingType, sortingChanged: boolean) =>
  sortingChanged ? TYPE_TO_STRING_ID[sortingType] : 'DV_DP_EPISODE_SORT_BY'

class EpisodeListComp extends React.Component<EpisodeListProps, EpisodeListState> {
  constructor(props: EpisodeListProps) {
    super(props)
    this.state = {
      episodesHidden: props.cardTitleIds.length < props.totalEpisodeSize,
      sortingState: SortingType.EpisodeNumberAscending, // Initially episodes are sorted in ascending episode order,
      sortingChanged: false
    }
  }

  componentDidMount() {

    const { requestContext, pageTitleId, enableLeavingSoonBtf, episodesLeavingSoon, isElcano, collectionType,
            totalEpisodeSize, cardTitleIds, episodesSeen, logger } = this.props

    /* If not all the episodes have been fetched */
    if (collectionType === 'episodes' && cardTitleIds.length < totalEpisodeSize) {
      /* We auto-expand the list if the episodes seen exceeds the Threshold */
      if (episodesSeen >= EXPANDER_THRESHOLD) {
        requestEpisodes(requestContext.rawContext, pageTitleId, isElcano, EXPANDER_THRESHOLD,
          totalEpisodeSize - EXPANDER_THRESHOLD)
        this.setState({
          episodesHidden: false
        })
      }

      if (enableLeavingSoonBtf && episodesLeavingSoon > 0) {
        logger.count('DVWebNode::Detail::BtfLeavingSoonMessages', episodesLeavingSoon, true)
      }
    }
  }

  shouldComponentUpdate(nextProps: EpisodeListProps, nextState: EpisodeListState) {
    return nextProps.cardTitleIds !== this.props.cardTitleIds ||
      nextState.sortingState !== this.state.sortingState ||
      nextState.episodesHidden !== this.state.episodesHidden
  }

  onClickHandler = (pageType?: WpsRuntime.atv.wps.PageType, subPageType?: WpsRuntime.atv.wps.SubPageType, sortingType?: SortingType) =>
    (e: React.MouseEvent<HTMLElement> | React.MouseEvent<HTMLAnchorElement>) => {
    const { requestContext, pageTitleId, isElcano, totalEpisodeSize, cardTitleIds } = this.props

    e.preventDefault() /* Prevents redirect */

    // If a new sorting options is chosen or the expander is clicked, we log a reftag
    if (!sortingType || this.state.sortingState !== sortingType) {
      logRefTag(sortingType ? TYPE_TO_REFTAG[sortingType] : ref('atv_dp_ep_exp'), pageType, subPageType)
    }

    this.setState(prevState => {
      return sortingType && prevState.sortingState === sortingType? prevState : {
        episodesHidden: false,
        sortingState: sortingType || prevState.sortingState,
        sortingChanged: prevState.sortingChanged || sortingType !== undefined
      }
    })
    if (cardTitleIds.length < totalEpisodeSize) {
      requestEpisodes(requestContext.rawContext, pageTitleId, isElcano, EXPANDER_THRESHOLD,
        totalEpisodeSize - EXPANDER_THRESHOLD)
    }
  }

  render() {
    const { header, cardTitleIds, totalEpisodeSize, collectionType, pageTitleId, requestContext, titleIdToAvailable } = this.props

    const { episodesHidden, sortingState, sortingChanged } = this.state

    /* We show the spinner if every episode hasn't been added to the store yet */
    const showSpinner = cardTitleIds.length < totalEpisodeSize

    if (!collectionType) {
      return null
    }

    const titleIds = collectionType === 'episodes' && !showSpinner ?
      TYPE_TO_SORT_FUNC[sortingState](cardTitleIds, titleIdToAvailable) : cardTitleIds

    return <DpWidgetContext.Consumer>
      {({getString, pageType, subPageType}) => {
        const cardComponents =
        titleIds.map((titleId, idx) => {
          const episodeProps = {
            titleId,
            collectionType,
            epListIndex: `${collectionType}-${idx}`,
            isFirstEpisode: idx === 0,
            isBonus: collectionType === "bonus"
          }
          return (
            <React.Fragment key={`episode-${titleId}-${idx}`}>
              <Episode {...episodeProps} />
            </React.Fragment>
          )
        })

        if ((cardComponents || []).length === 0) {
          return null
        }

        const dropListLinks = TYPE_LIST.map((sortingType) => {
          const isSelected = sortingType === sortingState
          const stringId = TYPE_TO_STRING_ID[sortingType]

          return {
            id: sortingType,
            name: sortingType,
            text:
              <span className={isSelected? st.boldText : ''}>
                <TextLocalizer stringID={stringId} />
              </span>,
            isHighlighted: isSelected,
            onClick: this.onClickHandler(pageType, subPageType, sortingType),
            ariaLabel: getString(stringId)
          } as LinkProps
        })

        /* Url for Core Experience */
        const params = {
          get: {
            "episodeListSize": `${totalEpisodeSize}`
          }
        } as MakeUrlArgs
        const pageUrl = requestContext.makeUrl(detailPagePath(pageTitleId), params)

        /**
         * If the number of episodes seen exceeds the Threshold number then we show the Episode expander
         * else we show all the episodes (the list auto-expands)
         */
        if (collectionType === 'episodes') {
          return (
            <div className={st.pageSection}>
              <div className={st.headerWithDropList}>
                { header && <h1 className={st.episodeListHeader}>{header} ({totalEpisodeSize})</h1> }
                <CloseableDropList links={dropListLinks} customDropListID={DROP_LIST_ID} isCustomTrigger={true}
                                   right={true} customText={getString(dropdownText(sortingState, sortingChanged))} />
              </div>
              <ol className={st.episodeList}>
                {cardComponents.slice(EPISODE_START_INDEX, EXPANDER_THRESHOLD)}
                <span className={episodesHidden? st.hidden : ''}>
                  {showSpinner?
                    <div className={st.episodeSpinner}><Spinner /></div> :
                    cardComponents.slice(EXPANDER_THRESHOLD)
                  }
                </span>
              </ol>
              { episodesHidden &&
              <Link className={st.expanderLink} href={pageUrl} onClick={this.onClickHandler(pageType, subPageType)} data-automation-id={'ep-expander'}
                    data-testid={'ep-expander'} ariaLabel={getString('DV_DP_EPISODE_EXPANDER', { episodeCount: `${totalEpisodeSize}` })}>
                  <TextLocalizer stringID={'DV_DP_EPISODE_EXPANDER'} replace={{ episodeCount: `${totalEpisodeSize}` }} />
              </Link> }
            </div>
          )
        } else {
          const paginationArgs = {
            totalEpisodeSize,
            collectionType,
          }
          return (
            <div className={st.pageSection}>
              { header &&
              <h1 className={st.episodeListHeader}>{header} ({totalEpisodeSize})</h1>}
              <ol className={st.episodeList}>{cardComponents}</ol>
              <CollectionPagination {...paginationArgs} />
            </div>
          )
        }
      }}
    </DpWidgetContext.Consumer>
  }
}

const mapStateToProps = (state: IDetailPageState): IStateProps => ({
  requestContext: sanitizedRequestContextSelector(state),
  pageTitleId: state.pageTitleId,
  isElcano: Boolean(state.features.isElcano),
  episodesSeen: episodesSeenSelector(state),
  titleIdToAvailable: episodesAvailableSelector(state),
  episodesLeavingSoon: episodesLeavingSoonSelector(state),
  enableLeavingSoonBtf: state.features.enableLeavingSoonBtf
})

export const EpisodeList = withLogger(connect(mapStateToProps)(EpisodeListComp))

